import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';

type MasterMultiCheckboxGroupProps = {
  label: string;
  options: DynamicEntityRow[];
  value: DynamicEntityRow[];
  onChange: (rows: DynamicEntityRow[]) => void;
  getOptionLabel: (row: DynamicEntityRow | null | undefined) => string;
  loading?: boolean;
  disabled?: boolean;
  renderOptionLabel?: (row: DynamicEntityRow) => React.ReactNode;
  /** Title tetap terlihat saat scroll di dalam section group aktif. Default true. */
  stickyLabel?: boolean;
  /**
   * Dipanggil 3 detik setelah pencarian lokal tidak menampilkan apa pun.
   * Hasilnya ditambahkan ke daftar section ini.
   */
  onSearchMiss?: (query: string) => Promise<DynamicEntityRow[]>;
  /** Saat kotak cari kosong, tampilkan semua item terpilih plus sebanyak ini yang belum terpilih. */
  unselectedVisibleLimit?: number;
  /** Label di atas, input cari selebar penuh. Dipakai modal checklist di layar kecil. */
  compactSearch?: boolean;
  'data-testid'?: string;
};

/** Multi-select via checkbox list (replaces Autocomplete multiple on sampling-plan forms). */
export function MasterMultiCheckboxGroup({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  loading = false,
  disabled = false,
  renderOptionLabel,
  stickyLabel = true,
  onSearchMiss,
  unselectedVisibleLimit,
  compactSearch = false,
  'data-testid': dataTestId,
}: MasterMultiCheckboxGroupProps) {
  const [search, setSearch] = useState('');
  const [remoteRows, setRemoteRows] = useState<DynamicEntityRow[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(false);
  const [remoteSettled, setRemoteSettled] = useState(false);
  const onSearchMissRef = useRef(onSearchMiss);
  onSearchMissRef.current = onSearchMiss;
  const selectedIds = new Set(value.map((row) => row.documentId));
  const query = search.trim();
  const searchPattern = useMemo(() => {
    if (!query) return { regex: null as RegExp | null, invalid: false };
    try {
      return { regex: new RegExp(query, 'i'), invalid: false };
    } catch {
      return { regex: null, invalid: true };
    }
  }, [query]);

  function optionVisible(option: DynamicEntityRow): boolean {
    if (!query || searchPattern.invalid || !searchPattern.regex) return true;
    return searchPattern.regex.test(getOptionLabel(option));
  }

  const hasSearchMatch =
    Boolean(query) &&
    !searchPattern.invalid &&
    searchPattern.regex != null &&
    options.some((option) => searchPattern.regex!.test(getOptionLabel(option)));

  useEffect(() => {
    setRemoteRows([]);
    setRemoteError(false);
    setRemoteLoading(false);
    setRemoteSettled(false);
    if (!query || searchPattern.invalid || hasSearchMatch || !onSearchMissRef.current) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      const searchMiss = onSearchMissRef.current;
      if (!searchMiss || cancelled) return;
      setRemoteLoading(true);
      void searchMiss(query)
        .then((rows) => {
          if (!cancelled) setRemoteRows(rows);
        })
        .catch(() => {
          if (!cancelled) {
            setRemoteRows([]);
            setRemoteError(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setRemoteLoading(false);
            setRemoteSettled(true);
          }
        });
    }, 3000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, searchPattern.invalid, hasSearchMatch]);

  const remoteIds = useMemo(() => new Set(remoteRows.map((row) => row.documentId)), [remoteRows]);
  const mergedOptions = useMemo(
    () => [...options.filter((row) => !remoteIds.has(row.documentId)), ...remoteRows],
    [options, remoteIds, remoteRows],
  );
  const orderedOptions = useMemo(() => {
    const byId = new Map<string, DynamicEntityRow>();
    for (const row of mergedOptions) {
      if (row.documentId && !byId.has(row.documentId)) byId.set(row.documentId, row);
    }
    for (const row of value) {
      if (row.documentId && !byId.has(row.documentId)) byId.set(row.documentId, row);
    }
    const selected: DynamicEntityRow[] = [];
    const selectedIdSet = new Set<string>();
    for (const row of value) {
      const resolved = byId.get(row.documentId);
      if (!resolved?.documentId || selectedIdSet.has(resolved.documentId)) continue;
      selectedIdSet.add(resolved.documentId);
      selected.push(resolved);
    }
    const rest = [...byId.values()].filter((row) => !selectedIdSet.has(row.documentId));
    return [...selected, ...rest];
  }, [mergedOptions, value]);

  const limitedVisibleIds = (() => {
    if (unselectedVisibleLimit == null || query) return null;
    const ids = new Set<string>();
    let unselectedCount = 0;
    for (const option of orderedOptions) {
      const id = option.documentId;
      if (!id) continue;
      if (selectedIds.has(id)) {
        ids.add(id);
        continue;
      }
      if (unselectedCount < unselectedVisibleLimit) {
        ids.add(id);
        unselectedCount += 1;
      }
    }
    return ids;
  })();

  function rowVisible(option: DynamicEntityRow): boolean {
    if (limitedVisibleIds) return limitedVisibleIds.has(option.documentId);
    if (selectedIds.has(option.documentId)) return true;
    if (remoteIds.has(option.documentId)) return true;
    return optionVisible(option);
  }

  function toggle(row: DynamicEntityRow, checked: boolean) {
    if (checked) {
      if (selectedIds.has(row.documentId)) return;
      onChange([...value, row]);
      return;
    }
    onChange(value.filter((item) => item.documentId !== row.documentId));
  }

  // Pakai `div` (bukan fieldset): `position: sticky` di dalam fieldset sering diabaikan browser.
  return (
    <FormControl
      component="div"
      variant="standard"
      disabled={disabled}
      fullWidth
      sx={{ display: 'block' }}
      data-testid={dataTestId}
    >
      <Box
        sx={
          stickyLabel
            ? {
                position: 'sticky',
                top: '-17px',
                zIndex: 2,
                bgcolor: 'background.paper',
                pt: '20px',
                pb: '20px',
                mb: 0.25,
                borderBottom: 1,
                borderColor: 'divider',
              }
            : { mb: 0.75 }
        }
      >
        <Box
          sx={
            compactSearch
              ? { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.75 }
              : { display: 'flex', alignItems: 'center', gap: 1.5 }
          }
        >
          <FormLabel
            component="div"
            sx={{ typography: 'body2', fontWeight: 600, lineHeight: 1.4, flexShrink: 0 }}
          >
            {label}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, ml: 0.75 }}
            >
              ({selectedIds.size}/{options.length})
            </Typography>
          </FormLabel>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={compactSearch ? 'Cari' : 'ketik disini untuk mencari'}
            size="small"
            fullWidth
            disabled={disabled || loading}
            inputProps={{ 'aria-label': `Cari ${label}` }}
            sx={compactSearch ? { minWidth: 0, width: '100%' } : undefined}
          />
        </Box>
      </Box>
      {loading && options.length === 0 ? (
        <Box display="flex" alignItems="center" gap={1} py={0.5}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">
            Memuat opsi…
          </Typography>
        </Box>
      ) : (
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 1,
            rowGap: 0.25,
            alignItems: 'start',
          }}
        >
          {searchPattern.invalid ? (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="error">
                Pola regex tidak valid.
              </Typography>
            </Box>
          ) : null}
          {options.length === 0 && remoteRows.length === 0 && !query && !remoteLoading ? (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">
                Tidak ada opsi.
              </Typography>
            </Box>
          ) : null}
          {query &&
          !searchPattern.invalid &&
          !hasSearchMatch &&
          remoteRows.length === 0 &&
          !remoteLoading &&
          (!onSearchMiss || remoteSettled) ? (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.secondary">
                {remoteError ? 'Gagal mencari.' : 'Tidak ada opsi yang cocok.'}
              </Typography>
            </Box>
          ) : null}
          {remoteLoading ? (
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Mencari…
              </Typography>
            </Box>
          ) : null}
          {orderedOptions.map((option) => {
            const checked = selectedIds.has(option.documentId);
            const visible = rowVisible(option);
            return (
              <FormControlLabel
                key={option.documentId}
                sx={{
                  display: visible ? 'flex' : 'none',
                  alignItems: 'flex-start',
                  mr: 0,
                  my: 0.15,
                  minWidth: 0,
                  borderRadius: 1,
                  px: 0.5,
                  mx: -0.5,
                  bgcolor: checked ? 'action.selected' : 'transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: checked ? 'action.selected' : 'action.hover',
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={(e) => toggle(option, e.target.checked)}
                    disabled={disabled}
                    sx={{ pt: 0.35 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ pt: 0.35, display: 'block', wordBreak: 'break-word' }}
                  >
                    {renderOptionLabel ? renderOptionLabel(option) : getOptionLabel(option)}
                  </Typography>
                }
              />
            );
          })}
        </FormGroup>
      )}
    </FormControl>
  );
}
